// Match Lab label-review helpers from the labeling wizard: the
// ball-position candidate shapes (wizard-candidates endpoint), the
// "same moment" windows, and this browser's memory of dismissed
// candidates. The wizard's own queue and panel were merged into the label
// review (MatchLabLabelMode.tsx; Ben, 2026-10-22).
/** event_labels.side — kept local: importing it from MatchLab.tsx would
 *  make this module and its host circular. */
type Side = 'home' | 'away'

/** A candidate within this of an existing label of its type counts as
 *  already labeled: a goal's zone window and its label are seconds
 *  apart, never frame-equal. */
export const NEAR_LABEL_S = 12
/** A zone window this close to a detected event of the pass is the same
 *  moment — the detected one (which knows its team) represents it. */
export const NEAR_DETECTED_S = 8
export type WizardLabel = {
  id: number
  frame: number
  event_type: string
  side: string | null
}

export type ZoneCandidate = {
  frame: number
  end_frame: number
  side: Side | null
  kind: 'goal' | 'corner' | 'throw_in' | 'goal_kick' | 'kick_off'
  clip_start: number
  clip_end: number
  /** Kick-offs only: 'both' — the ball on the centre spot AND the teams
   *  lined up in their halves (wizard_candidates.strong_kick_offs);
   *  'restart' — the teams lined up, read from the players alone, the
   *  ball too poorly tracked to agree (wizard_candidates.restart_kick_offs:
   *  no kicker, the scorer by the goal end). Corners only: 'crowd' — a
   *  penalty area crowded and still, read from the players
   *  (wizard_candidates.crowd_corners). Absent where the players could
   *  not be read: the ball's zone alone. */
  cue?: 'both' | 'restart' | 'crowd'
  /** Strong kick-offs only (wizard_candidates.strong_kick_offs): the last
   *  ball at a goal before the kick, which end, and the scorer each cue
   *  guesses — `scorer` only where the two agree. `side` is the kicker. */
  goal_frame?: number | null
  goal_end?: '+x' | '-x' | null
  scorer_by_end?: Side | null
  scorer_by_kicker?: Side | null
  scorer?: Side | null
  /** Where the goal before a kick-off is looked for: before the estimate
   *  to the kick (wizard_candidates.goal_clip) — the estimate lands up to
   *  20 s after the goal, or long before it. Null with no estimate. */
  goal_clip_start?: number | null
  goal_clip_end?: number | null
}

// ── rejected-candidate memory (localStorage, per game) ──

const rejectedKey = (gameId: number) => `matchlab-wizard-rejected-${gameId}`

export function readRejected(gameId: number): Set<string> {
  try {
    const raw = localStorage.getItem(rejectedKey(gameId))
    const arr: unknown = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(arr) ? arr.filter((v) => typeof v === 'string') : [])
  } catch {
    return new Set()
  }
}

export function writeRejected(gameId: number, rejected: Set<string>) {
  try {
    localStorage.setItem(rejectedKey(gameId), JSON.stringify([...rejected]))
  } catch { /* storage full / disabled: rejects just do not persist */ }
}
